import { defineStore } from "pinia";
import { MediaLibraryState } from "@/stores/media-library/types.ts";
import { useApiStore } from "@/stores/api/index.ts";
import { transformMedia } from "@/util/mediaHelper.v2.ts";

const useMediaLibraryStore = defineStore("media-library", {
  state: (): MediaLibraryState => ({
    mediaItems: [],
    initialMediaItems: [],
    isLoading: false,
    isRecentlyUploadedCollapsed: false,
    error: null,
    pagination: {
      totalItems: 0,
      totalPages: 0,
      currentPage: 1,
      pageSize: 24,
    },
    hasMore: true,
    searchQuery: "",
    searchType: "text",
    tags: [],
    selectedMediaItem: null,
    abortController: null,
    filter: "all",
  }),
  actions: {
    async fetchMediaItems() {
      this.isLoading = true;

      this.abortController?.abort();
      this.abortController = new AbortController();

      try {
        const response = await this.api.searchMedia({
          page: this.pagination.currentPage,
          limit: this.pagination.pageSize,
          textQuery: this.searchQuery,
          vectorQuery: this.searchQuery,
          tags: this.tags,
          signal: this.abortController.signal,
          hideInDAM: this.filter === "deleted",
        });

        this.mediaItems = response.data.media.map(transformMedia);

        if (!this.initialMediaItems.length) {
          this.initialMediaItems = response.data.media.map(transformMedia);
        }

        this.pagination.totalPages = response.data.pagination.totalPages;
        this.hasMore = this.pagination.currentPage < this.pagination.totalPages;
        this.error = null;
      } catch (error) {
        this.error =
          error instanceof Error ? error.message : "An unknown error occurred";
      } finally {
        this.isLoading = false;
      }
    },
    async deleteMediaItem(id: string) {
      await this.api.patchMediaMetaData(
        {
          hideInDAM: true,
        },
        id
      );

      this.selectedMediaItem = null;
      this.fetchMediaItems();
    },
    async restoreMediaItem(id: string) {
      await this.api.patchMediaMetaData(
        {
          hideInDAM: false,
        },
        id
      );

      this.selectedMediaItem = null;
      this.filter = "all";
      this.fetchMediaItems();
    },
  },
  getters: {
    api() {
      return useApiStore();
    },
  },
});

export { useMediaLibraryStore };
